import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

type ApplyMode = 'merge' | 'replace';

interface TemplatePreview {
  resource_label?: { singular: string; plural: string };
  resources_summary?: string;
  table_mix?: Array<{ count: number; capacity: number }>;
  total_seats?: number;
  not_included?: string[];
}

interface BranchTemplate {
  id: string;
  name: string;
  description: string;
  comingSoon: boolean;
  preview: TemplatePreview | null;
}

interface AdminTemplatesProps {
  getToken: () => Promise<string>;
  onApplied: () => void;
}

const Icons = {
  Check: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  X: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Alert: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Utensils: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
    </svg>
  ),
  Scissors: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3" />
      <circle cx="6" cy="18" r="3" />
      <line x1="20" y1="4" x2="8.12" y2="15.88" />
      <line x1="14.47" y1="14.48" x2="20" y2="20" />
      <line x1="8.12" y1="8.12" x2="12" y2="12" />
    </svg>
  ),
  Stethoscope: () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
      <path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4" />
      <circle cx="20" cy="10" r="2" />
    </svg>
  ),
};

function templateIcon(id: string) {
  if (id === 'restaurant') return <Icons.Utensils />;
  if (id === 'friseur') return <Icons.Scissors />;
  if (id === 'praxis') return <Icons.Stethoscope />;
  return <Icons.Utensils />;
}

export default function AdminTemplates({ getToken, onApplied }: AdminTemplatesProps) {
  const [templates, setTemplates] = useState<BranchTemplate[]>([]);
  const [activeTemplate, setActiveTemplate] = useState<string | null>(null);
  const [resourceCount, setResourceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [dialogTemplate, setDialogTemplate] = useState<BranchTemplate | null>(null);
  const [applyMode, setApplyMode] = useState<ApplyMode | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const hasExistingResources = resourceCount > 0;

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [catalogRes, resourcesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/templates`, { headers }),
        axios.get(`${API_BASE_URL}/admin/resources`, { headers }),
      ]);
      setTemplates(catalogRes.data.templates || []);
      setActiveTemplate(catalogRes.data.active_template || null);
      setResourceCount((resourcesRes.data.resources || []).length);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Templates konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 6000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const openDialog = (template: BranchTemplate) => {
    setDialogTemplate(template);
    setApplyMode(hasExistingResources ? null : 'replace');
    setApplyError(null);
  };

  const closeDialog = () => {
    if (applying) return;
    setDialogTemplate(null);
    setApplyMode(null);
    setApplyError(null);
  };

  const handleApply = async () => {
    if (!dialogTemplate || !applyMode) return;
    setApplying(true);
    setApplyError(null);
    try {
      const token = await getToken();
      await axios.post(
        `${API_BASE_URL}/admin/templates/apply`,
        { template_id: dialogTemplate.id, mode: applyMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveTemplate(dialogTemplate.id);
      setDialogTemplate(null);
      setApplyMode(null);
      setSuccessMessage('Template aktiv. Öffnungszeiten und Terminlängen jetzt unter Einstellungen festlegen.');
      await load();
      onApplied();
    } catch (err: any) {
      setApplyError(err.response?.data?.error || 'Template konnte nicht aktiviert werden.');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-[#E8EAF6] border-t-[#FF6D00] rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-400 font-medium">Lade Templates...</p>
        </div>
      </div>
    );
  }

  if (error && templates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-red-500"><Icons.Alert /></div>
          <p className="text-sm text-[#0E0B1E] font-semibold mb-3">{error}</p>
          <button
            type="button"
            onClick={() => load()}
            className="px-5 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)]"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 shadow-sm"
          >
            <div className="w-6 h-6 bg-emerald-500 rounded-lg flex items-center justify-center text-white flex-shrink-0"><Icons.Check /></div>
            <p className="text-sm font-semibold text-emerald-800">{successMessage}</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-[18px] border border-[#E8EAF6] shadow-[0_2px_12px_rgba(14,11,30,0.04)] px-6 py-5">
        <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Branch-Templates</div>
        <p className="text-xs text-[#654F6F] mt-1.5 leading-relaxed max-w-2xl">
          Starte mit passenden Ressourcen für deine Branche. Ein Template setzt nur Labels und Tische — Öffnungszeiten und Terminlängen stellst du danach selbst unter Einstellungen ein.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates.map((template) => {
          const isActive = activeTemplate === template.id;
          const disabled = template.comingSoon;
          return (
            <div
              key={template.id}
              className={cn(
                "bg-white rounded-[18px] border overflow-hidden shadow-[0_2px_12px_rgba(14,11,30,0.04)] flex flex-col",
                isActive ? "border-[#FF6D00]/40" : "border-[#E8EAF6]",
                disabled && "opacity-70"
              )}
            >
              <div className="h-1 w-full" style={{ background: disabled ? '#E8EAF6' : 'linear-gradient(90deg, #FF6D00, #FF9A00)' }} />
              <div className="p-5 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: disabled ? '#F4F5FC' : 'linear-gradient(135deg, #FF6D00, #FF9A00)',
                        color: disabled ? '#654F6F' : '#fff',
                        boxShadow: disabled ? 'none' : '0 4px 12px rgba(255,109,0,0.25)',
                      }}
                    >
                      {templateIcon(template.id)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{template.name}</div>
                      <div className="text-xs text-[#654F6F] mt-0.5">{template.description}</div>
                    </div>
                  </div>
                  {isActive && (
                    <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0">
                      Aktiv
                    </span>
                  )}
                  {disabled && (
                    <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wide bg-[#F4F5FC] text-[#654F6F] border border-[#E8EAF6] flex-shrink-0">
                      Demnächst
                    </span>
                  )}
                </div>

                {template.preview && (
                  <div className="mt-1 mb-4 space-y-2">
                    {template.preview.resource_label && (
                      <div className="text-[12px] text-[#0E0B1E]">
                        <span className="font-bold">Bezeichnung:</span>{' '}
                        {template.preview.resource_label.singular} / {template.preview.resource_label.plural}
                      </div>
                    )}
                    {template.preview.resources_summary && (
                      <div className="text-[12px] text-[#0E0B1E]">
                        <span className="font-bold">Tische:</span> {template.preview.resources_summary}
                      </div>
                    )}
                    {template.preview.table_mix && (
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {template.preview.table_mix.map((mix) => (
                          <span
                            key={`${mix.capacity}-${mix.count}`}
                            className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-[#F4F5FC] text-[#654F6F] border border-[#E8EAF6]"
                          >
                            {mix.count}× {mix.capacity} Pers.
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-[#654F6F] leading-relaxed pt-1">
                      Öffnungszeiten und Terminlängen stellst du danach selbst unter Einstellungen ein.
                    </p>
                  </div>
                )}

                {isActive && !disabled && (
                  <p className="text-[11px] text-[#654F6F] mb-4">Du kannst alles danach unter Einstellungen anpassen.</p>
                )}

                <div className="mt-auto">
                  {disabled ? (
                    <button
                      type="button"
                      disabled
                      className="w-full px-4 py-2.5 bg-[#F4F5FC] text-gray-400 text-sm font-bold rounded-xl cursor-not-allowed"
                    >
                      Demnächst
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openDialog(template)}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] hover:shadow-[0_6px_18px_rgba(255,109,0,0.35)] transition-all"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {isActive ? 'Erneut anwenden' : 'Template aktivieren'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <AnimatePresence>
        {dialogTemplate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#0E0B1E]/40 backdrop-blur-md"
              onClick={closeDialog}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full max-w-lg rounded-2xl shadow-[0_24px_64px_rgba(14,11,30,0.2)] relative z-10 border border-[#E8EAF6] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain"
            >
              <div className="px-6 py-5 bg-gradient-to-r from-[#FF6D00]/6 to-transparent border-b border-[#E8EAF6] flex justify-between items-center">
                <h3 className="text-base font-bold text-[#0E0B1E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {activeTemplate === dialogTemplate.id ? 'Template erneut anwenden' : `${dialogTemplate.name}-Template aktivieren`}
                </h3>
                <button type="button" onClick={closeDialog} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400">
                  <Icons.X />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-[#654F6F] mb-2">Wird geschrieben</p>
                  <ul className="text-sm text-[#0E0B1E] space-y-1.5">
                    {dialogTemplate.preview?.resource_label && (
                      <li>Bezeichnung: <strong>{dialogTemplate.preview.resource_label.singular} / {dialogTemplate.preview.resource_label.plural}</strong></li>
                    )}
                    {dialogTemplate.preview?.resources_summary && (
                      <li>{dialogTemplate.preview.resources_summary}</li>
                    )}
                  </ul>
                </div>

                {dialogTemplate.preview?.not_included && dialogTemplate.preview.not_included.length > 0 && (
                  <div className="bg-[#F4F5FC] rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#654F6F] mb-1.5">Wird nicht verändert</p>
                    <p className="text-xs text-[#654F6F]">{dialogTemplate.preview.not_included.join(' · ')}</p>
                  </div>
                )}

                {hasExistingResources && (
                  <div>
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                      <p className="text-xs text-amber-800 font-medium">
                        Du hast bereits {resourceCount} {resourceCount === 1 ? 'Ressource' : 'Ressourcen'}. Wähle, wie das Template damit umgehen soll.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-2">
                      <button
                        type="button"
                        onClick={() => setApplyMode('merge')}
                        className={cn(
                          "text-left px-4 py-3 rounded-xl border transition-colors",
                          applyMode === 'merge' ? "border-[#FF6D00] bg-[#FF6D00]/6" : "border-[#E8EAF6] hover:bg-[#F4F5FC]"
                        )}
                      >
                        <div className="text-sm font-bold text-[#0E0B1E]">Ergänzen</div>
                        <div className="text-xs text-[#654F6F] mt-0.5">Bestehende Ressourcen behalten, fehlende Standard-Tische dazulegen.</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setApplyMode('replace')}
                        className={cn(
                          "text-left px-4 py-3 rounded-xl border transition-colors",
                          applyMode === 'replace' ? "border-[#FF6D00] bg-[#FF6D00]/6" : "border-[#E8EAF6] hover:bg-[#F4F5FC]"
                        )}
                      >
                        <div className="text-sm font-bold text-[#0E0B1E]">Ersetzen</div>
                        <div className="text-xs text-[#654F6F] mt-0.5">Alle bestehenden Ressourcen durch den Restaurant-Tischmix ersetzen. Geht nicht, wenn noch Termine darauf liegen.</div>
                      </button>
                    </div>
                  </div>
                )}

                {applyError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                    <div className="text-red-500 flex-shrink-0 mt-0.5"><Icons.Alert /></div>
                    <p className="text-xs font-semibold text-red-800">{applyError}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button
                    type="button"
                    onClick={closeDialog}
                    disabled={applying}
                    className="flex-1 px-4 py-2.5 bg-[#F4F5FC] text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    disabled={!applyMode || applying}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#FF6D00] to-[#FF9A00] text-white text-sm font-bold rounded-xl shadow-[0_4px_12px_rgba(255,109,0,0.25)] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  >
                    {applying ? 'Wird angewendet…' : 'Bestätigen'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
